/* eslint-disable no-param-reassign */

import {castDraft, Draft} from 'immer';
import {last} from 'lodash';

import {isNotNull} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {isAtomValidWhenItsReferencing} from './expressionAtomHelpers';
import {ClientAtom, FunctionAtom, isFunctionAtom} from './expressionAtomModel';
import {buildDefaultName, inferAtom} from './expressionInferHelpers';
import {getTypeMismatchError, referenceNotFoundError} from './expressionLabels';
import {brandExpressionId, ExpressionId} from './expressionModel';
import {createExpressionsContext, ExpressionsContext} from './expressionsContext';
import {ExpressionDependencies, ExpressionState, ExpressionValidator} from './expressionState';
import {areInferredTypesEqual, UnknownResult} from './infer/expressionResultModel';

export function getExpressionWithId(state: Draft<ExpressionState>, id: ExpressionId) {
  if (state.expressionPanelState.selectedExpression?.id === id) {
    return state.expressionPanelState.selectedExpression;
  }

  return state.expressions.find((exp) => exp.id === id);
}

export function allocateExpressionId(state: Draft<ExpressionState>): ExpressionId {
  // Store the maximum allocated ID so we don't reuse IDs during a session.
  state.maxId++;

  return brandExpressionId(`local:${state.maxId}`);
}

export function inferAndValidateExpressions<S extends ExpressionState>(
  draft: Draft<S>,
  dependencies: ExpressionDependencies,
  validator?: ExpressionValidator<S>,
) {
  const {expressions, expressionPanelState} = draft;

  const allExpressions = [...expressions, expressionPanelState.selectedExpression].filter(isNotNull);

  const context = createExpressionsContext({
    expressions: allExpressions,
    customFields: draft.customFields,
    initialInfer: draft.initialInfer,
  });

  allExpressions.forEach((expression) => {
    inferAndValidateAtoms(expression.atoms, context, dependencies);
    const lastAtom = last(expression.atoms);

    if (!lastAtom) {
      expression.inferredType = UnknownResult;
      return;
    }

    const newInferredType = lastAtom.inferredType;

    expression.inferredType = newInferredType;
    const newDefaultName = buildDefaultName(expression.atoms, context, dependencies);

    if (newDefaultName) {
      expression.defaultName = newDefaultName;
    }
  });

  if (validator) {
    validator(draft);
  }
}

function inferAndValidateAtoms(
  atoms: Draft<ReadonlyArray<ClientAtom>>,
  context: ExpressionsContext,
  dependencies: ExpressionDependencies,
) {
  atoms.forEach((atom, i) => {
    if (isFunctionAtom(atom)) {
      inferAndValidateAtoms(atom.functionAtoms, context, dependencies);
    }

    const newInferredType = inferAtom(atom, context, dependencies);
    if (!areInferredTypesEqual(newInferredType, atom.inferredType)) {
      atom.inferredType = newInferredType;
    }

    const previousAtom = atoms[i - 1];
    if (previousAtom) {
      const isValid =
        areInferredTypesEqual(atom.previousInfer, previousAtom.inferredType) ||
        // We need to mark as error only first atom.
        !previousAtom.isValid;

      updateAtomValidity(atom, isValid, () =>
        getTypeMismatchError(atom.previousInfer, previousAtom.inferredType),
      );
    } else {
      const isValid = isAtomValidWhenItsReferencing(atom, context.expressions);
      updateAtomValidity(atom, isValid, () => referenceNotFoundError);
    }
  });
}

function updateAtomValidity(atom: Draft<ClientAtom>, isValid: boolean, errorBuilder: () => string) {
  if (atom.isValid === isValid) {
    return;
  }

  atom.isValid = isValid;
  atom.errorMessage = isValid ? undefined : errorBuilder();
}

export function addAtomToFunction(
  functionAtom: Draft<FunctionAtom>,
  atom: ClientAtom,
  context: ExpressionsContext,
  dependencies: ExpressionDependencies,
) {
  const {functionAtoms} = functionAtom;
  functionAtoms.push(castDraft(atom));

  // Infer function atom again.
  functionAtom.inferredType = inferAtom(functionAtom, context, dependencies);
}

export function getAtomPosition(
  atoms: Draft<ReadonlyArray<ClientAtom>>,
  atomClientId: string,
): [number, Draft<ReadonlyArray<ClientAtom>>] | undefined {
  const index = atoms.findIndex((atom) => atom.clientId === atomClientId);
  if (index >= 0) {
    return [index, atoms];
  }

  let position;

  atoms.some((atom) => {
    position = atom.functionAtoms && getAtomPosition(atom.functionAtoms, atomClientId);

    return Boolean(position);
  });

  return position;
}
